import React, { useEffect, useRef, useState } from 'react';
import { COLORS } from '../../../core/constants/colors';
import { EnrichmentSession } from '../types';

// ─── Step definitions ────────────────────────────────────────────────────────

const STEPS = [
  {
    label: 'Analyzing',
    messages: [
      'Reading document content…',
      'Extracting key information…',
      'Parsing text and metadata…',
    ],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    color: COLORS.extended.azure,
  },
  {
    label: 'Processing',
    messages: [
      'Running AI field scoring…',
      'Evaluating completeness…',
      'Matching against question bank…',
    ],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    color: COLORS.primary.softLime,
  },
  {
    label: 'Generating',
    messages: [
      'Building your question form…',
      'Finalising field assessments…',
      'Preparing recommendations…',
    ],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    color: COLORS.extended.orange,
  },
] as const;

/** Map progress_percent → index of the active step (0 | 1 | 2). */
function getActiveStepIndex(progress: number): number {
  if (progress >= 67) return 2;
  if (progress >= 34) return 1;
  return 0;
}

/**
 * How full the connector after step[index] should be (0–100).
 * Each connector spans one third of the total progress range.
 *   connector 0: progress 0 → 33
 *   connector 1: progress 33 → 67
 */
function getConnectorFill(connectorIndex: number, progress: number): number {
  const rangeSize = 100 / STEPS.length;        // ~33.3
  const start = connectorIndex * rangeSize;
  const end = (connectorIndex + 1) * rangeSize;
  return Math.min(Math.max((progress - start) / (end - start), 0), 1) * 100;
}

// ─── ConnectorBar ─────────────────────────────────────────────────────────────

interface ConnectorBarProps {
  fillPercent: number;
  color: string;
}

const ConnectorBar: React.FC<ConnectorBarProps> = ({ fillPercent, color }) => (
  <div
    style={{
      position: 'relative',
      width: 32,
      height: 2,
      backgroundColor: '#E5E7EB',
      marginBottom: 20,
      flexShrink: 0,
      borderRadius: 2,
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        width: `${fillPercent}%`,
        backgroundColor: color,
        borderRadius: 2,
        transition: 'width 0.6s ease',
      }}
    />
  </div>
);

// ─── StepIndicator ───────────────────────────────────────────────────────────

type StepState = 'pending' | 'active' | 'done';

interface StepIndicatorProps {
  step: (typeof STEPS)[number];
  state: StepState;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ step, state }) => {
  const isDone = state === 'done';
  const isActive = state === 'active';
  const isPending = state === 'pending';

  const bgColor = isPending ? '#F3F4F6' : step.color + '20';
  const iconColor = isPending ? '#9CA3AF' : step.color;
  const labelColor = isPending ? '#9CA3AF' : COLORS.primary.persianIndigo;

  return (
    <div className="flex flex-col items-center space-y-2">
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center"
        style={{
          backgroundColor: bgColor,
          animation: isActive ? 'pulse 1.5s ease-in-out infinite' : undefined,
        }}
      >
        {isDone ? (
          <svg className="w-6 h-6" style={{ color: iconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <span style={{ color: iconColor }}>{step.icon}</span>
        )}
      </div>
      <span className="text-xs font-semibold" style={{ color: labelColor }}>{step.label}</span>
    </div>
  );
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface ProcessingStepProps {
  enrichmentCompleted: boolean;
  processingAfterSubmit: boolean;
  processingMessage: string;
  session: EnrichmentSession | null;
  sessionError: string | null;
  checkingStatus: boolean;
  loadingForm: boolean;
  documentCount?: number;
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
  onBack: () => void;
  onViewSession: () => void;
  onSkipEnrichment: () => void;
  onCancelSession: () => void;
  onLoadForm: () => void;
  onSkipToDashboard: () => void;
}

export const ProcessingStep: React.FC<ProcessingStepProps> = ({
  enrichmentCompleted,
  processingAfterSubmit,
  processingMessage,
  session,
  sessionError,
  checkingStatus,
  loadingForm,
  documentCount = 0,
  primaryActionLabel = 'Fill Missing Fields',
  secondaryActionLabel = 'Skip to Dashboard',
  onBack,
  onViewSession,
  onSkipEnrichment,
  onCancelSession,
  onLoadForm,
  onSkipToDashboard,
}) => {
  const startedAt = useRef<number>(Date.now());
  const [elapsed, setElapsed] = useState(0);
  const [msgIndex, setMsgIndex] = useState(0);

  const progress = session?.progress_percent ?? 0;
  const activeStepIndex = enrichmentCompleted ? STEPS.length : getActiveStepIndex(progress);

  // Elapsed timer — tick every second while processing
  useEffect(() => {
    if (enrichmentCompleted) return;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 1000);
    return () => clearInterval(id);
  }, [enrichmentCompleted]);

  // Rotate messages every 4 s, reset when active step changes
  useEffect(() => {
    if (enrichmentCompleted) return;
    setMsgIndex(0);
    const id = setInterval(() => {
      setMsgIndex(i => (i + 1) % STEPS[Math.min(activeStepIndex, STEPS.length - 1)].messages.length);
    }, 4000);
    return () => clearInterval(id);
  }, [activeStepIndex, enrichmentCompleted]);

  const currentMessage = enrichmentCompleted
    ? ''
    : STEPS[Math.min(activeStepIndex, STEPS.length - 1)].messages[msgIndex];

  const contextLine = documentCount > 0
    ? `Analyzing ${documentCount} document${documentCount !== 1 ? 's' : ''} across all fields`
    : 'Your enrichment session is running in the background. This typically takes 30–60 seconds.';

  // LOADING STATE - While waiting for enrichment to complete
  if (!enrichmentCompleted) {
    return (
      <div className="flex-1 py-8">
        {!processingAfterSubmit && (
          <div className="mb-6">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:bg-gray-100"
              style={{ color: COLORS.primary.persianIndigo }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>
        )}

        {/* Modern Loading Animation */}
        <div className="relative w-32 h-32 mx-auto mb-8">
          {/* Outer rotating ring */}
          <div 
            className="absolute inset-0 rounded-full border-4 border-t-transparent animate-spin"
            style={{ 
              borderColor: COLORS.extended.azure,
              borderTopColor: 'transparent',
              animationDuration: '1.5s'
            }}
          ></div>
          
          {/* Middle rotating ring - opposite direction */}
          <div 
            className="absolute inset-3 rounded-full border-4 border-b-transparent"
            style={{ 
              borderColor: COLORS.primary.softLime,
              borderBottomColor: 'transparent',
              animation: 'spin 2s linear infinite reverse'
            }}
          ></div>
          
          {/* Inner pulsing circle — shows live progress % */}
          <div
            className="absolute inset-8 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: COLORS.extended.azure + '20',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }}
          >
            <span style={{ color: COLORS.extended.azure, fontSize: 13, fontWeight: 700, lineHeight: 1 }}>
              {progress > 0 ? `${progress}%` : ''}
            </span>
          </div>
          
          {/* Orbiting dots */}
          <div className="absolute inset-0" style={{ animation: 'spin 3s linear infinite' }}>
            <div 
              className="absolute top-0 left-1/2 w-3 h-3 rounded-full -ml-1.5"
              style={{ backgroundColor: COLORS.extended.azure }}
            ></div>
          </div>
          <div className="absolute inset-0" style={{ animation: 'spin 3s linear infinite', animationDelay: '1s' }}>
            <div 
              className="absolute top-0 left-1/2 w-3 h-3 rounded-full -ml-1.5"
              style={{ backgroundColor: COLORS.primary.softLime }}
            ></div>
          </div>
          <div className="absolute inset-0" style={{ animation: 'spin 3s linear infinite', animationDelay: '2s' }}>
            <div 
              className="absolute top-0 left-1/2 w-3 h-3 rounded-full -ml-1.5"
              style={{ backgroundColor: COLORS.extended.orange }}
            ></div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: COLORS.primary.persianIndigo }}>
            {processingMessage}
          </h2>
          <p className="text-sm mb-1" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
            {processingAfterSubmit
              ? 'Please wait while we finalise your enrichment session…'
              : contextLine}
          </p>

          {/* Elapsed timer */}
          {!processingAfterSubmit && (
            <p className="text-xs mb-6" style={{ color: COLORS.primary.persianIndigo, opacity: 0.45 }}>
              Running for {elapsed}s
            </p>
          )}

          {/* State-aware step indicators with progress-driven connectors */}
          <div className="flex items-center justify-center mb-4">
            {STEPS.map((step, i) => {
              const state: StepState =
                i < activeStepIndex ? 'done' : i === activeStepIndex ? 'active' : 'pending';
              return (
                <React.Fragment key={step.label}>
                  <StepIndicator step={step} state={state} />
                  {i < STEPS.length - 1 && (
                    <ConnectorBar
                      fillPercent={getConnectorFill(i, progress)}
                      color={step.color}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Rotating contextual message */}
          {!processingAfterSubmit && currentMessage && (
            <p
              key={currentMessage}
              className="text-sm mb-6"
              style={{ color: COLORS.primary.persianIndigo, opacity: 0.6, minHeight: 20 }}
            >
              {currentMessage}
            </p>
          )}
        </div>

        {sessionError && (
          <div className="max-w-md mx-auto mt-4 p-3 rounded-lg" style={{ backgroundColor: COLORS.extended.redSalsa + '20' }}>
            <p className="text-sm text-center" style={{ color: COLORS.extended.redSalsa }}>
              {sessionError}
            </p>
          </div>
        )}
      </div>
    );
  }

  // COMPLETED STATE - After enrichment finishes
  return (
    <div className="flex-1 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Header with Animation and Back Button */}
        <div className="mb-8">
          {!processingAfterSubmit && (
            <div className="mb-6">
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:bg-gray-100"
                style={{ color: COLORS.primary.persianIndigo }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            </div>
          )}
          <div className="flex justify-center mb-6">
            <div className="relative inline-block">
              <div className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: COLORS.primary.softLime + '40', animationDuration: '2s' }}></div>
              <div
                className="relative w-24 h-24 rounded-full flex items-center justify-center shadow-lg"
                style={{ backgroundColor: COLORS.primary.softLime }}
              >
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-3" style={{ color: COLORS.primary.persianIndigo }}>
              {processingAfterSubmit ? 'Finalization Complete! 🎉' : 'Enrichment Complete! '}
            </h1>
            <p className="text-base max-w-2xl mx-auto" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
              {processingAfterSubmit 
                ? 'Your enrichment session has been finalized successfully! Redirecting to dashboard...'
                : "We've analyzed your documents and generated personalized questions to help improve your company profile."}
            </p>
          </div>
        </div>

        {session && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {/* Completeness Score Card */}
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg p-6 border-2 border-transparent hover:border-opacity-50 transition-all" style={{ borderColor: COLORS.extended.azure + '20' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.extended.azure + '20' }}>
                    <svg className="w-5 h-5" style={{ color: COLORS.extended.azure }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-sm" style={{ color: COLORS.primary.persianIndigo }}>
                    Completeness
                  </h3>
                </div>
                <div className="mb-2">
                  <span className="text-4xl font-bold" style={{ color: COLORS.extended.azure }}>
                    {Math.round(session.current_score)}
                  </span>
                  <span className="text-2xl font-bold" style={{ color: COLORS.extended.azure, opacity: 0.6 }}>%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${session.current_score}%`,
                      backgroundColor: COLORS.extended.azure,
                    }}
                  />
                </div>
                <p className="text-xs" style={{ color: COLORS.primary.persianIndigo, opacity: 0.6 }}>
                  {session.current_score >= 80 ? 'Great score!' : 'Room for improvement'}
                </p>
              </div>

              {/* Progress Card */}
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg p-6 border-2 border-transparent hover:border-opacity-50 transition-all" style={{ borderColor: COLORS.primary.softLime + '20' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.primary.softLime + '20' }}>
                    <svg className="w-5 h-5" style={{ color: COLORS.primary.softLime }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-sm" style={{ color: COLORS.primary.persianIndigo }}>
                    Progress
                  </h3>
                </div>
                <div className="mb-2">
                  <span className="text-4xl font-bold" style={{ color: COLORS.primary.softLime }}>
                    {Math.round(session.progress_percent)}
                  </span>
                  <span className="text-2xl font-bold" style={{ color: COLORS.primary.softLime, opacity: 0.6 }}>%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${session.progress_percent}%`,
                      backgroundColor: COLORS.primary.softLime,
                    }}
                  />
                </div>
                <p className="text-xs" style={{ color: COLORS.primary.persianIndigo, opacity: 0.6 }}>
                  Overall enrichment progress
                </p>
              </div>

              {/* Session Status Card */}
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg p-6 border-2 border-transparent hover:border-opacity-50 transition-all" style={{ borderColor: COLORS.extended.orange + '20' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.extended.orange + '20' }}>
                    <svg className="w-5 h-5" style={{ color: COLORS.extended.orange }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-sm" style={{ color: COLORS.primary.persianIndigo }}>
                    Status
                  </h3>
                </div>
                <div className="mb-3">
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-sm font-semibold"
                    style={{ 
                      backgroundColor: COLORS.primary.softLime + '20',
                      color: COLORS.primary.softLime
                    }}
                  >
                    {session.status === 'active' ? 'Active' : session.status}
                  </span>
                </div>
                <p className="text-xs" style={{ color: COLORS.primary.persianIndigo, opacity: 0.6 }}>
                  Try count: {session.try_count}
                </p>
              </div>
            </div>

            {/* Next Steps Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: COLORS.extended.azure + '15' }}>
                  <svg className="w-6 h-6" style={{ color: COLORS.extended.azure }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ color: COLORS.primary.persianIndigo }}>
                    What's Next?
                  </h2>
                  <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                    Choose how you'd like to proceed
                  </p>
                </div>
              </div>

              {sessionError && (
                <div className="mb-6 p-4 rounded-xl border-2" style={{ backgroundColor: COLORS.extended.redSalsa + '10', borderColor: COLORS.extended.redSalsa + '30' }}>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.extended.redSalsa }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm font-medium" style={{ color: COLORS.extended.redSalsa }}>
                      {sessionError}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Primary Action - Load Form */}
                <button
                  onClick={onLoadForm}
                  disabled={loadingForm}
                  className="group relative overflow-hidden rounded-xl p-6 transition-all hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-left"
                  style={{ backgroundColor: COLORS.extended.azure }}
                >
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-white bg-opacity-20 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </div>
                      <svg className="w-6 h-6 text-white transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">
                      {loadingForm ? 'Loading...' : primaryActionLabel}
                    </h3>
                    <p className="text-sm text-white text-opacity-90">
                      Complete the enrichment form to improve your profile score
                    </p>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                </button>

                {/* Secondary Action - Skip */}
                <button
                  onClick={onSkipToDashboard}
                  disabled={checkingStatus}
                  className="group relative overflow-hidden rounded-xl p-6 transition-all hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-left border-2"
                  style={{ 
                    borderColor: COLORS.extended.orange,
                    backgroundColor: 'white'
                  }}
                >
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.extended.orange + '15' }}>
                        <svg className="w-5 h-5" style={{ color: COLORS.extended.orange }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <svg className="w-6 h-6 transition-transform group-hover:translate-x-1" style={{ color: COLORS.extended.orange }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: COLORS.primary.persianIndigo }}>
                      {checkingStatus ? 'Loading...' : secondaryActionLabel}
                    </h3>
                    <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                      Continue with your current score and finalize later
                    </p>
                  </div>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

